import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce/lib';
import { useMarketingSearch } from '../../../hooks/useMarketingPreferences';
import GroupsHierarchy from './GroupsHierarchy';
import { ExclamationInTriangle, Rectangle } from '@td-client-frontend/icons';
import MarketingMessage, { MarketingMessageType } from './MarketingMessage';
import MarketingSearch, { MarketingSearchType } from './MarketingSearch';
import { MarketingHierarchyData, MarketingHierarchyRowData } from '../../../@types/Marketing';
import { PendingChangesContext } from '../../../context/PendingChangesContext';
import { Nullable } from '../../../@types/common';

interface Props {
  selectedNode: Nullable<MarketingHierarchyRowData>;
  onNodeChange: (data: Nullable<MarketingHierarchyRowData>) => void;
}

const MarketingSearchNavigation: FC<Props> = ({ selectedNode, onNodeChange }) => {
  const { checkAndPrompt } = useContext(PendingChangesContext);

  const [searchBy, setSearchBy] = useState<MarketingSearchType>(MarketingSearchType.GROUP);
  const [searchValue, setSearchValue] = useState<string>('');
  const [previouslySelctedNode, setPreviouslySelctedNode] = useState<Nullable<MarketingHierarchyRowData>>(null);

  const [debouncedSearchValue] = useDebounce(searchValue.length >= 3 ? searchValue : '', 500);
  const { data, isFetching } = useMarketingSearch(searchBy, debouncedSearchValue);

  const handleOnNodeChange = useCallback(
    (value) => {
      if (value) setPreviouslySelctedNode(value);
      onNodeChange(value);
    },
    [onNodeChange]
  );

  const onSearchValueChange = (value: string) => {
    checkAndPrompt(() => {
      setSearchValue(value);
      if (value.length < 3) {
        handleOnNodeChange(null);
      }
    });
  };

  const onSearchTypeChange = (type: MarketingSearchType) => {
    checkAndPrompt(() => {
      setSearchBy(type);
      handleOnNodeChange(null);
    });
  };

  const handleEntitySelect = useCallback(
    (entity: MarketingHierarchyRowData) => {
      checkAndPrompt(() => {
        handleOnNodeChange(entity);
      });
    },
    [checkAndPrompt, handleOnNodeChange]
  );

  useEffect(() => {
    if (isFetching) handleOnNodeChange(null);
  }, [handleOnNodeChange, isFetching]);

  const getPreselectedOrgById = useCallback(
    (rows: MarketingHierarchyData[], id: string): MarketingHierarchyData | undefined => {
      for (const row of rows) {
        if (row.id === id) {
          return row;
        }
        const firstMatched = getPreselectedOrgById(Array.isArray(row.subRows) ? row.subRows : [], id);
        if (firstMatched) return firstMatched;
      }
    },
    []
  );

  useEffect(() => {
    if (data && previouslySelctedNode && !isFetching) {
      const previousOrg = getPreselectedOrgById(data?.tableData, previouslySelctedNode.id);
      handleOnNodeChange(previousOrg);
    } else if (data && !selectedNode && !isFetching) {
      // Recursively walks provided array drilling down into subRows and storing any record that has no subRows or subRows has no length
      const findGroupRecursive = (
        arr: MarketingHierarchyData[],
        record: MarketingHierarchyData
      ): MarketingHierarchyData[] => {
        const { subRows } = record;
        if (!subRows || !subRows.length) {
          arr.push(record);
          return arr;
        }

        return arr.concat(subRows.reduce(findGroupRecursive, []));
      };

      const { tableData = [] } = data;
      const groups: MarketingHierarchyData[] = tableData.reduce(findGroupRecursive, []);
      if (groups.length) {
        handleOnNodeChange(groups[0]);
      } else if (tableData) {
        handleOnNodeChange(tableData[0]);
      }
    }
  }, [data, isFetching, handleOnNodeChange, selectedNode, previouslySelctedNode, getPreselectedOrgById]);

  const marketingMessage = useMemo(() => {
    switch (true) {
      case isFetching:
        return MarketingMessageType.LOADING;
      case searchValue.length < 3:
        return MarketingMessageType.MINIMAL_LENGTH;
      case data?.isOverflowed:
        return MarketingMessageType.TO_MANY_RESULTS;
      case !data?.tableData?.length:
        return MarketingMessageType.NO_RESULTS_FOUND;
      default:
        return MarketingMessageType.DEFAULT;
    }
  }, [data, isFetching, searchValue.length]);

  return (
    <div className="w-full sm:w-2/5">
      <div className="overflow-hidden px-4 sm:px-2 md:px-4 pt-4 bg-gray-10 font-effra">
        <MarketingSearch
          onSearchTermChange={onSearchValueChange}
          onSearchTypeChange={onSearchTypeChange}
          searchValue={searchValue}
          searchType={searchBy}
        />
        {searchValue && (
          <div className="pt-0 mb-2 bg-white border-1 border-gray-20 shadow">
            {marketingMessage ? (
              <MarketingMessage messageType={marketingMessage} />
            ) : (
              <GroupsHierarchy
                data={data!.tableData}
                searchTerm={debouncedSearchValue}
                searchType={searchBy}
                selectedEntity={selectedNode}
                onEntitySelect={handleEntitySelect}
              />
            )}
          </div>
        )}
      </div>
      {searchValue && !marketingMessage && (
        <div className="mt-4 ml-3 text-gray-700">
          <div className="flex items-center pb-2">
            <Rectangle aria-hidden="true" className="w-8" />
            <span>Customized settings applied</span>
          </div>
          <div className="flex items-center">
            <ExclamationInTriangle aria-hidden="true" className="w-8" />
            <span>Excluded from marketing</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingSearchNavigation;
